import { Box } from '@mui/material'
import { IdentityEvents } from '@serverlessinc/core-events'
import { useAuthUrlQuery } from 'auth/hook/useAuthUrlQuery'
import { session } from 'auth/session'
import { authCallback, nonBrowserCallback } from 'auth/utils'
import { apiClient } from 'common/api'
import LoadingPage from 'common/components/LoadingPage'
import { usePageTitle } from 'common/hooks/usePageTitle'
import { serverless } from 'common/sdk'
import { Event } from 'common/tracking'
import { publishEvent } from 'common/utils/events'
import { startCase } from 'lodash'
import queryString from 'query-string'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useMount } from 'react-use'
import { RegisterAndSignInView } from './RegisterAndSignInView'
import { UserNameView } from './UserNameView'
import { IconServerlessFrameworkText } from '_app/icons/IconServerlessFrameworkText'

/**
 * Authentication Page
 *
 * This one Component handles the "login/registration" page,
 * the "forgot password" page, the OAuth callback from Auth0,
 * and handles callbacks to CLI/non-browser clients.
 *
 * The user registration/login flow includes:
 *
 * - Processing query params to see if the user is logging in via CLI, UTM lead gen codes, pre-selected Template, etc...
 * - Filling out the register/login form or selecting a social option.
 * - Quickly forwarding to Auth0 and returning back to this view w/ a ?code= param.  This is known as the "callback" view.
 * - Note: The Auth0 permission screen is only shown when running this app in localhost.
 * - Exchange the "code" for "tokens" (access, refresh, id) from Auth0.
 * - Check to see if the User exists in the SF database.
 * - If User does not exist (they are new), show a form to enter a custom Username (which is their default Org name).
 * - Validate the User and Org name are both available in the SF database.  Then create them.
 * - Create a session in the User's browser.
 * - Optionally post a message via websockets to any non-browser clients the User was trying to log in (i.e. the SF CLI, the future GUI).
 * - Redirect them to the Dashboard homescreen, or wherever they were trying to go before.  This is currently saved in the session.
 */

/**
 * Authentication
 */
export const Authentication = () => {
  const { pathname, search } = useLocation()

  /**
   * Initialize event tracking
   */
  const event = new Event()

  const { query, setQuery } = useAuthUrlQuery()
  usePageTitle(`${startCase(query.view)}`)
  /**
   * Check for a matching template/component in the query parameters
   */
  if (query.package) {
    event.set('package', query.package)
  }

  /**
   * If you going to /join then you're going to be in the 'join organization'
   * flow. Unfortunately, the invitation token is put into the query parameters
   * as 'code' which is the same query parameter name as the auth0 token callack
   * code.
   */
  const isJoinOrganizationFlow = pathname === '/join'

  /**
   * Set initial state to loading if a code is in the URL, but not for the join flow
   */
  const [loading, setLoading] = useState(!isJoinOrganizationFlow && query.code)

  useMount(() => {
    async function run() {
      /**
       * If no 'code', the user has landed on the register/login page...
       */
      if (!query.code || isJoinOrganizationFlow) {
        session.set('authClient', query.client)
        session.set('authClientTransactionId', query.transactionId)

        /**
         * Save URL to redirect to after authentication in the session.
         * Also save the query parameters... these are trickier because
         * we need to distinguish app query params vs. auth0 callback
         * and marketing ones.
         */
        if (pathname !== '/') {
          session.set('authRedirectPath', pathname)
        }

        /**
         * Set UTM tracking parameters
         */
        session.set('referrerSource', query.utm_source)
        session.set('referrerMedium', query.utm_medium)
        session.set('referrerCampaign', query.utm_campaign)
        session.set('referrerContent', query.utm_content)
        session.set('referrerTerm', query.utm_term)

        /**
         * Track Package (if exists) so we know what originally brought this user to us
         */

        session.set('referrerPackage', query.package)

        // Track
        let event = new Event({
          name: 'auth_visit',
          client: query.client || 'browser',
        })
        event.publish()
      }

      /**
       * In the join flow, and there is an invitation code present...
       * Save the token into the session so if you auth, the token
       * is saved as part of the query parameter when the app loads
       */
      if (query.code && isJoinOrganizationFlow) {
        session.set('authJoinOrganizationInvitationToken', query.code)
        // Track
        let event = new Event({
          name: 'auth_visit_invite',
        })
        event.publish()
      }

      /**
       * Save query parameters
       */
      if (!query.code) {
        session.set('authRedirectQuery', search)
      }

      /**
       * If 'code' query parameter exists, the Authentication page is handling a callback from Auth0
       */
      if (query.code && !isJoinOrganizationFlow) {
        setLoading(true)

        // Track
        let callbackEvent = new Event({
          name: 'auth_callback',
        })
        callbackEvent.publish()

        /**
         *  Exchange auth0 callback "code" for "tokens"
         */
        try {
          const { access_token, refresh_token, id_token, expires_in } = await authCallback(
            query.code,
            window.location.origin
          )

          /**
           * Clear out query parameters that we don't want to carry through to the
           * logged in experience
           */
          setQuery(
            {
              code: undefined,
              state: undefined,
              transactionId: undefined,
              utm_source: undefined,
              utm_medium: undefined,
              utm_content: undefined,
              utm_term: undefined,
              view: undefined,
            },
            'replaceIn'
          )

          let me = null

          /**
           * Update Serverless SDK w/ authorized token
           */
          serverless.config({ accessKey: id_token })

          /**
           * Persist the current session to localStorage
           */
          session.login({
            authAccessToken: access_token,
            authRefreshToken: refresh_token,
            authIdToken: id_token,
            /**
             * Nubmer of seconds token is good for
             */
            authExpires: expires_in,
            /**
             * Current UNIX timestamp in milliseconds
             */
            authTimeGranted: Date.now(),
          })

          /**
           * Try to fetch your own information
           *
           * Note: if you are registering as a new user, then this request will 404
           */
          try {
            me = await apiClient({
              url: 'core/me',
              headers: {
                Authorization: `Bearer ${id_token}`,
              },
            })

            /**
             * Save the current user to the session
             */
            session.set('user', me)
          } catch (error) {
            /**
             * We got a 404, we need the user to specify an 'username' to finish account setup
             */
            if (error.message?.includes('404')) {
              setQuery({ view: 'username' })
              setLoading(false)
            }

            /**
             * We can't continue, since the user doesn't have an org setup yet
             */
            return
          }

          /**
           * If that works, then get the orgs for the user
           */
          const orgs = await apiClient({
            url: 'core/tenants',
            params: {
              userName: me.userName,
            },
            headers: {
              Authorization: `Bearer ${id_token}`,
            },
          })

          /**
           * CLI login
           *
           * If a non-browser-based client is trying to log in, perform callback via websockets.
           */
          if (session.get('authClient') === 'cli' && session.get('authClientTransactionId')) {
            await nonBrowserCallback(session.get('authClientTransactionId'), {
              access_token: session.get('authAccessToken'),
              id_token: session.get('authIdToken'),
              refresh_token: session.get('authRefreshToken'),
              expires_at: session.get('authExpires'),
              username: me.userName,
              user_uid: me.userUid,
            })
          }

          /**
           * Hopefully there should be a case where you're an owner of some org
           */
          let currentOrg = orgs.find((org) => org.role === 'owner')

          /**
           * If there's some weird case where you're not an owner, just pick the first org
           */
          if (!currentOrg) {
            currentOrg = orgs[0]
          }

          const path = session.get('authRedirectPath') || `/${currentOrg.orgName}/apps`

          /**
           * Tracking (Google Analytics)
           */
          let event = new Event({
            name: 'auth_login',
            orgName: currentOrg.orgName,
          })
          event.publish()

          /**
           * Piece together a query string if it was present before the auth. Let's
           * scrub unwanted/marketing params, too
           */
          let q = queryString.parse(session.get('authRedirectQuery'))

          delete q.code
          delete q.state
          delete q.utm_source
          delete q.utm_medium
          delete q.utm_campaign
          delete q.utm_content
          delete q.utm_term
          delete q.view
          delete q.socialAuthType

          /**
           * Set the invitation code for /join
           */
          if (session.get('authJoinOrganizationInvitationToken')) {
            q.code = session.get('authJoinOrganizationInvitationToken')
          }

          /**
           * Tracking (Event bus)
           */
          await publishEvent(
            new IdentityEvents.UserAuthenticatedV1Event({
              userId: session.get('user').userUid,
              queryParameters: q,
            })
          )

          const stringQuery = queryString.stringify(q)
          q = stringQuery ? `?${stringQuery}` : ''

          /**
           * Hard refresh to show the app
           */

          window.location.href = `${path}${q}`
        } catch (e) {
          setQuery({ code: undefined, state: undefined }, 'replaceIn')
          setLoading(false)
        }
      }
    }

    run()
  })

  if (loading) {
    return <LoadingPage />
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          margin: 'auto',
          alignItems: 'stretch',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 4,
          width: 450,
        }}
      >
        <IconServerlessFrameworkText
          sx={{
            width: '70%',
            height: 'auto',
            margin: '0 auto',
          }}
        />
        {query.view === 'username' ? (
          <UserNameView />
        ) : (
          <RegisterAndSignInView setLoading={setLoading} />
        )}
      </Box>
    </Box>
  )
}

export default Authentication
